// Three bespoke full-screen generator animations, one per Ronin coach.
// Each FX is rendered while the multipla is loading and matches the
// personality of its master.
//
//  🛡️ Kurosawa → meditative zen: ink brush + ripples + kanji "禅"
//  ⚔️ Mifune    → duo-katana duel: blades clash with sparks + kanji "武"
//  🔥 Naruse    → brutal assault: multi-slash + blood splats + kanji "鬼"
//
// All three expose the same API: `<KurosawaFX active />`. Duration is baked
// into the keyframes so the parent only needs to set `active`.

const ZEN_KANJI = ["禅", "道", "静", "心"];

function KurosawaFX({ active }) {
  if (!active) return null;
  const C = "#9DF6FF";
  const glow = "rgba(157,246,255,0.5)";
  return (
    <div
      data-testid="ronin-fx-kurosawa"
      aria-hidden
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        pointerEvents: "none", overflow: "hidden",
        background: "radial-gradient(circle at 50% 50%, rgba(8,16,20,0.72) 0%, rgba(4,6,8,0.92) 70%)",
        animation: "kuroFadeOut 2200ms ease-in-out forwards",
      }}
    >
      {/* Zen ripples emanating from the centre */}
      {[0, 250, 500, 750].map((delay) => (
        <div
          key={delay}
          style={{
            position: "absolute", top: "50%", left: "50%",
            width: 40, height: 40, marginLeft: -20, marginTop: -20,
            borderRadius: "50%", border: `2px solid ${C}`,
            boxShadow: `0 0 14px ${glow}`,
            animation: `kuroRipple 1800ms ease-out ${delay}ms both`,
          }}
        />
      ))}
      {/* Calligraphic brush stroke from left to right */}
      <div style={{
        position: "absolute", top: "58%", left: "10%", right: "10%",
        height: 3,
        background: `linear-gradient(to right, transparent 0%, ${C} 40%, #fff 50%, ${C} 60%, transparent 100%)`,
        boxShadow: `0 0 12px ${C}, 0 0 24px ${glow}`,
        transformOrigin: "left center",
        animation: "kuroBrush 1600ms cubic-bezier(0.2, 0.7, 0.1, 1) 300ms both",
      }} />
      {/* Kanji grid — four characters meditating in sequence */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        display: "flex", gap: "2.2rem",
        fontFamily: "'Barlow Condensed', serif", fontWeight: 900,
        fontSize: "min(12vw, 140px)", lineHeight: 1,
        color: "var(--bc-ink, #fff)",
        textShadow: `0 0 12px ${C}, 0 0 30px ${glow}`,
      }}>
        {ZEN_KANJI.map((k, i) => (
          <span key={i} style={{
            animation: `kuroGlyph 1600ms cubic-bezier(0.2, 0.7, 0.1, 1) ${i * 180 + 200}ms both`,
            opacity: 0,
          }}>{k}</span>
        ))}
      </div>
    </div>
  );
}

function MifuneFX({ active }) {
  if (!active) return null;
  const C = "#F6C643";
  const glow = "rgba(246,198,67,0.6)";
  // Spark angles fanning out from the centre clash point
  const sparks = Array.from({ length: 12 }).map((_, i) => {
    const angle = (i * 30) * Math.PI / 180;
    return { dx: Math.cos(angle) * 260, dy: Math.sin(angle) * 260, delay: i * 20 };
  });
  return (
    <div
      data-testid="ronin-fx-mifune"
      aria-hidden
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        pointerEvents: "none", overflow: "hidden",
        background: "radial-gradient(circle at 50% 50%, rgba(30,20,0,0.65) 0%, rgba(6,4,0,0.92) 70%)",
      }}
    >
      {/* Flash at the moment of the clash */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(circle at 50% 50%, ${C}88 0%, ${C}33 30%, transparent 60%)`,
        animation: "mifFlash 1800ms ease-out forwards",
      }} />
      {/* Left katana slashing from top-left */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        width: "70vw", height: 4, marginLeft: "-35vw",
        background: `linear-gradient(to right, transparent 0%, ${C} 40%, #fff 50%, ${C} 60%, transparent 100%)`,
        boxShadow: `0 0 18px ${C}, 0 0 36px ${glow}`,
        animation: "mifSlashL 900ms cubic-bezier(0.2, 0.7, 0.15, 1) both",
      }} />
      {/* Right katana slashing from top-right */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        width: "70vw", height: 4, marginLeft: "-35vw",
        background: `linear-gradient(to right, transparent 0%, ${C} 40%, #fff 50%, ${C} 60%, transparent 100%)`,
        boxShadow: `0 0 18px ${C}, 0 0 36px ${glow}`,
        animation: "mifSlashR 900ms cubic-bezier(0.2, 0.7, 0.15, 1) 80ms both",
      }} />
      {/* Sparks fanning out */}
      {sparks.map((s, i) => (
        <div key={i} style={{
          position: "absolute", top: "50%", left: "50%",
          width: 6, height: 6, borderRadius: "50%",
          background: "#fff",
          boxShadow: `0 0 10px ${C}, 0 0 20px ${glow}`,
          "--dx": `${s.dx}px`, "--dy": `${s.dy}px`,
          animation: `mifSparks 900ms ease-out ${s.delay + 550}ms both`,
        }} />
      ))}
      {/* Final kanji punch-in */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        fontFamily: "'Barlow Condensed', serif", fontWeight: 900,
        fontSize: "min(22vw, 280px)", lineHeight: 1,
        color: "var(--bc-ink, #fff)",
        textShadow: `0 0 18px ${C}, 0 0 40px ${glow}, 0 0 80px ${glow}`,
        animation: "mifGlyph 1800ms cubic-bezier(0.2, 0.7, 0.1, 1) forwards",
        opacity: 0,
      }}>武</div>
    </div>
  );
}

function NaruseFX({ active }) {
  if (!active) return null;
  const C = "#FF4B5C";
  const glow = "rgba(255,75,92,0.55)";
  // Multi-angled slashes
  const slashes = [
    { top: "20%", rot: 15, delay: 0 },
    { top: "45%", rot: -10, delay: 120 },
    { top: "68%", rot: 20, delay: 240 },
    { top: "35%", rot: -25, delay: 360 },
  ];
  // Blood-splat particles
  const splats = Array.from({ length: 16 }).map((_, i) => {
    const angle = (i * 22.5 + Math.random() * 20) * Math.PI / 180;
    const mag = 200 + Math.random() * 180;
    return {
      dx: Math.cos(angle) * mag, dy: Math.sin(angle) * mag,
      size: 4 + Math.random() * 8,
      delay: 400 + i * 15,
    };
  });
  return (
    <div
      data-testid="ronin-fx-naruse"
      aria-hidden
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        pointerEvents: "none", overflow: "hidden",
        background: "radial-gradient(circle at 50% 50%, rgba(40,0,8,0.7) 0%, rgba(8,0,2,0.95) 70%)",
        animation: "narShake 1500ms ease-in-out",
      }}
    >
      {/* Aggressive red strobe */}
      <div style={{
        position: "absolute", inset: 0,
        background: `linear-gradient(180deg, ${C}1A 0%, transparent 50%, ${C}1A 100%)`,
        animation: "narStrobe 1500ms ease-out",
      }} />
      {/* Horizontal slashes across the screen */}
      {slashes.map((s, i) => (
        <div key={i} style={{
          position: "absolute", top: s.top, left: 0, right: 0,
          height: 4,
          background: `linear-gradient(to right, transparent 0%, ${C} 35%, #fff 48%, ${C} 52%, ${C}80 65%, transparent 100%)`,
          boxShadow: `0 0 18px ${C}, 0 0 36px ${glow}`,
          "--rot": `${s.rot}deg`,
          animation: `narSlash 600ms cubic-bezier(0.2, 0.7, 0.15, 1) ${s.delay}ms both`,
        }} />
      ))}
      {/* Blood-splat particles flying outwards */}
      {splats.map((s, i) => (
        <div key={i} style={{
          position: "absolute", top: "50%", left: "50%",
          width: s.size, height: s.size, borderRadius: "50%",
          background: C,
          boxShadow: `0 0 10px ${glow}`,
          "--dx": `${s.dx}px`, "--dy": `${s.dy}px`,
          animation: `narBloodSplat 1000ms ease-out ${s.delay}ms both`,
        }} />
      ))}
      {/* Central kanji — the Oni (demon) */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        fontFamily: "'Barlow Condensed', serif", fontWeight: 900,
        fontSize: "min(24vw, 320px)", lineHeight: 1,
        color: "var(--bc-ink, #fff)",
        textShadow: `0 0 18px ${C}, 0 0 40px ${glow}, 0 0 90px ${C}`,
        animation: "narGlyph 1500ms cubic-bezier(0.2, 0.7, 0.1, 1) forwards",
        opacity: 0,
      }}>鬼</div>
    </div>
  );
}

export function RoninGenerateFX({ coach, active }) {
  if (!active) return null;
  if (coach === "kurosawa") return <KurosawaFX active />;
  if (coach === "mifune")   return <MifuneFX   active />;
  if (coach === "naruse")   return <NaruseFX   active />;
  return null;
}
